import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import type { DashboardResumo, Board } from '../types'
import { api } from '../api'

export default function DashboardPage() {
  const [resumo, setResumo] = useState<DashboardResumo | null>(null)
  const [board, setBoard] = useState<Board | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([api.getDashboard(), api.getBoards()]).then(([r, bs]) => {
      setResumo(r)
      if (bs.length) setBoard(bs[0])
      setLoading(false)
    })
  }, [])

  if (loading || !resumo) {
    return <div className="flex items-center justify-center h-full text-gray-400">Carregando...</div>
  }

  // Agregar cards por coluna
  const porColuna: Record<string, number> = {}
  resumo.cards_por_coluna_categoria.forEach((r) => {
    porColuna[r.coluna_id] = (porColuna[r.coluna_id] ?? 0) + r.total
  })

  const totalCards = Object.values(porColuna).reduce((a, b) => a + b, 0)
  const decisoesPendentes = resumo.decisoes_por_status.find((d) => d.status === 'proposta')?.total ?? 0
  const milestonesAtivos = resumo.milestones_por_status.reduce((a, m) => a + m.total, 0)

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Painel de Expansao</h1>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard label="Cards Ativos" value={totalCards} color="blue" />
        <StatCard label="Atrasados" value={resumo.cards_atrasados} color={resumo.cards_atrasados > 0 ? 'red' : 'green'} />
        <StatCard label="Decisoes Pendentes" value={decisoesPendentes} color={decisoesPendentes > 0 ? 'yellow' : 'green'} />
        <StatCard label="Milestones" value={milestonesAtivos} color="purple" />
      </div>

      {/* Cards por coluna */}
      {board && (
        <div className="bg-white rounded-xl shadow-sm border p-5 mb-6">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Distribuicao por Coluna</h2>
          <div className="space-y-2">
            {board.colunas.map((col) => {
              const count = porColuna[col.id] ?? 0
              const pct = totalCards > 0 ? (count / totalCards) * 100 : 0
              return (
                <div key={col.id} className="flex items-center gap-3">
                  <span className="w-24 text-sm text-gray-600 shrink-0">{col.nome}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: col.cor }} />
                  </div>
                  <span className="text-sm text-gray-500 w-8 text-right">{count}</span>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link to="/quadro" className="bg-white rounded-xl shadow-sm border p-5 hover:shadow-md transition-shadow">
          <span className="text-2xl mb-2 block">📋</span>
          <h3 className="font-semibold text-gray-800">Quadro Kanban</h3>
          <p className="text-sm text-gray-500 mt-1">Gerenciar cards e tarefas</p>
        </Link>
        <Link to="/decisoes" className="bg-white rounded-xl shadow-sm border p-5 hover:shadow-md transition-shadow">
          <span className="text-2xl mb-2 block">⚖️</span>
          <h3 className="font-semibold text-gray-800">Decisoes</h3>
          <p className="text-sm text-gray-500 mt-1">{decisoesPendentes} decisao(oes) pendente(s)</p>
        </Link>
        <Link to="/pesquisas" className="bg-white rounded-xl shadow-sm border p-5 hover:shadow-md transition-shadow">
          <span className="text-2xl mb-2 block">🔍</span>
          <h3 className="font-semibold text-gray-800">Pesquisas</h3>
          <p className="text-sm text-gray-500 mt-1">Dados de mercado e analises</p>
        </Link>
      </div>
    </div>
  )
}

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  const colors: Record<string, string> = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    yellow: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
  }
  return (
    <div className={`rounded-xl border p-4 ${colors[color] ?? colors.blue}`}>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-sm mt-1 opacity-80">{label}</p>
    </div>
  )
}
